import React, { useContext, useState } from 'react'
import classNames from 'classnames'
import { scroller } from 'react-scroll'
import { IdentityContext } from '#src/containers/Identity'
import {
  ConsumerReviewSearchSort,
  DepartmentCuratedActivityFeedComponent,
  BucketedDisplayFacetFragment,
  NavDepartmentsComponent,
  SingleConsumerReviewComponent
} from '#src/generated/graphql'
import numeral from 'numeral'
import Summary from './Summary'
import SchemaData from './SchemaData'
import { Element as ScrollElement } from 'react-scroll'
import ActivityFeedCard from '#src/components/ActivityFeedCard'
import { Select, CircleLoader, FullBlockLoader, Button } from '@deal/components'
import { generateGrid } from '#src/services/grid'
import { Link, useHistory } from 'react-router-dom'
import PageContent from '#src/components/PageContent'
import styles from './styles.css'
import SidebarAwareGrid from '../SidebarAwareGrid'

export enum FeedPlacement {
  REVIEWS_PAGE,
  EXPERT_PROFILE
}

interface ReviewsFeedProps {
  departmentIds?: string[]
  expertIds?: string[]
  className?: string
  disableSidebar?: boolean
  placement: FeedPlacement
}

const ReviewsFeed: React.SFC<ReviewsFeedProps> = ({
  departmentIds = [],
  expertIds = [],
  className,
  disableSidebar,
  placement
}) => {
  const { myself } = useContext(IdentityContext)
  const [feedSortBy, setFeedSortBy] = useState(ConsumerReviewSearchSort.RELEVANCE)
  const [currentRatingFilter, setRatingFilter] = useState<undefined | string>(undefined)

  const {
    replace: historyReplace,
    location: { hash, search, pathname },
    push: historyPush
  } = useHistory()

  /**
   * Check to see if there is a review ID in the url
   */
  const queryParams = new URLSearchParams(search)
  let reviewId: string | null
  if (queryParams.has('reviewId')) {
    reviewId = queryParams.get('reviewId')
  }

  /**
   * Remove the review ID and redirect back to where we currently were
   */
  const removeReviewId = () => {
    if (queryParams.has('reviewId')) {
      queryParams.delete('reviewId')

      historyPush(`${pathname}?${queryParams.toString()}`)
    }
  }

  return (
    <PageContent className={className}>
      <DepartmentCuratedActivityFeedComponent
        variables={{
          query: {
            departmentIds: departmentIds,
            expertIds: expertIds,
            facetedSearch: true,
            facets: [
              {
                name: 'rating',
                bucketValues: currentRatingFilter ? [`${currentRatingFilter}`] : []
              }
            ]
          },
          sortBy: feedSortBy
        }}
        notifyOnNetworkStatusChange
      >
        {({ data, loading, fetchMore }) => {
          if (
            data &&
            data.consumerExpertReviewConnection &&
            data.consumerExpertReviewConnection.edges.length > 0
          ) {
            const hasNextPage = data.consumerExpertReviewConnection.pageInfo.hasNextPage
            const { edges, pageInfo } = data.consumerExpertReviewConnection

            const customFetchMore = () => {
              const cursor = pageInfo.endCursor

              fetchMore({
                variables: {
                  after: cursor
                },
                updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                  if (!fetchMoreResult) {
                    return previousQueryResult
                  }

                  const existingIds = new Set(
                    previousQueryResult.consumerExpertReviewConnection.edges.map(
                      edge => edge.node.id
                    )
                  )
                  const newEdges = fetchMoreResult.consumerExpertReviewConnection.edges.filter(
                    edge => !existingIds.has(edge.node.id)
                  )
                  const pageInfo = fetchMoreResult.consumerExpertReviewConnection.pageInfo

                  return newEdges.length
                    ? {
                        __typename: previousQueryResult.__typename,
                        consumerExpertReviewConnection: {
                          __typename: previousQueryResult.consumerExpertReviewConnection.__typename,
                          facets: [...previousQueryResult.consumerExpertReviewConnection.facets],
                          count: previousQueryResult.consumerExpertReviewConnection.count,
                          edges: [
                            ...previousQueryResult.consumerExpertReviewConnection.edges,
                            ...newEdges
                          ],
                          pageInfo
                        }
                      }
                    : previousQueryResult
                }
              })
            }

            const totalReviews = data.consumerExpertReviewConnection.count
            const reviewSummaryBuckets = data.consumerExpertReviewConnection.facets.find(
              facet => facet.name === 'rating'
            ) as BucketedDisplayFacetFragment | undefined

            const defaultGrid = generateGrid([{ summary: 2 }, { filter: 2 }, { feed: 2 }])
            const smGrid = generateGrid([{ filter: 8 }, { summary: 3, feed: 5 }])
            const mdGrid = generateGrid([{ filter: 12 }, { summary: 4, feed: 8 }])
            const mdGridWithSidebar = generateGrid([{ filter: 12 }, { summary: 5, feed: 7 }])

            return (
              <>
                {expertIds.length === 1 ? (
                  <SchemaData
                    initialReviewSet={edges.slice(0, 10).map(edge => edge.node)}
                    expertId={expertIds[0]}
                  />
                ) : (
                  <SchemaData
                    initialReviewSet={edges.slice(0, 10).map(edge => edge.node)}
                    departmentId={departmentIds[0]}
                  />
                )}
                <SidebarAwareGrid
                  areas={defaultGrid}
                  areasSm={smGrid}
                  areasMd={disableSidebar ? mdGrid : mdGridWithSidebar}
                  unless={disableSidebar || !myself}
                  className={styles.container}
                >
                  {({ Filter: FilterArea, Summary: SummaryArea, Feed: FeedArea }) => (
                    <>
                      <FilterArea>
                        {!myself && placement === FeedPlacement.REVIEWS_PAGE ? (
                          <NavDepartmentsComponent>
                            {({ data }) => {
                              if (data && data.departmentsForNavigation) {
                                return (
                                  <div className={styles.filters}>
                                    <Link to="/reviews" className={styles.filterLink}>
                                      <button
                                        className={classNames(styles.filterButton, {
                                          [styles.selected]: departmentIds.length === 0
                                        })}
                                      >
                                        All
                                      </button>
                                    </Link>
                                    {data.departmentsForNavigation.map(department => (
                                      <Link
                                        to={`/reviews/${department.slug}`}
                                        className={styles.filterLink}
                                        key={department.id}
                                      >
                                        <button
                                          className={classNames(styles.filterButton, {
                                            [styles.selected]: departmentIds.includes(department.id)
                                          })}
                                        >
                                          {department.displayName}
                                        </button>
                                      </Link>
                                    ))}
                                  </div>
                                )
                              }

                              return null
                            }}
                          </NavDepartmentsComponent>
                        ) : null}
                      </FilterArea>
                      <SummaryArea>
                        <Summary
                          reviewSummaryBuckets={reviewSummaryBuckets}
                          currentRatingFilter={currentRatingFilter}
                          onSetRatingFilter={value => {
                            setRatingFilter(value)
                          }}
                        />
                      </SummaryArea>
                      <FeedArea>
                        {reviewId ? (
                          <>
                            <div className={styles.feedHeader}>
                              <div className={styles.feedHeaderText}>
                                <div className={styles.feedHeaderInnerText}>
                                  Viewing <span>1</span> review of
                                  <span> {numeral(totalReviews).format('0,0')}</span>
                                </div>
                              </div>
                              <div className={styles.feedHeaderViewAll}>
                                <Button
                                  variant="secondary-link"
                                  size="xsmall"
                                  onClick={() => removeReviewId()}
                                  className={styles.removeReviewId}
                                >
                                  View all
                                </Button>
                              </div>
                            </div>
                            <div
                              className={classNames(styles.feedCards, {
                                [styles.withSidebar]: !disableSidebar || myself
                              })}
                            >
                              <SingleConsumerReviewComponent
                                variables={{
                                  id: reviewId
                                }}
                                onCompleted={() => {
                                  // Scroll to the highlighted review, removing the hash first and turning off scroll resetting
                                  if (hash === '#reviews') {
                                    historyReplace(`${location.pathname}${location.search}`, {
                                      resetScroll: false
                                    })

                                    scroller.scrollTo('featuredReview', {
                                      smooth: true,
                                      offset: -70
                                    })
                                  }
                                }}
                              >
                                {({ data, loading }) => {
                                  if (loading) {
                                    return <FullBlockLoader loader={CircleLoader} />
                                  }

                                  if (data && data.consumerExpertReview) {
                                    return (
                                      <ScrollElement name="featuredReview">
                                        <ActivityFeedCard
                                          activityFeedItem={data && data.consumerExpertReview}
                                          className={styles.feedItem}
                                        />
                                      </ScrollElement>
                                    )
                                  } else {
                                    return null
                                  }
                                }}
                              </SingleConsumerReviewComponent>
                            </div>
                            <div className={styles.fetchMoreButtonContainer}>
                              <Button variant="secondary-outline" onClick={() => removeReviewId()}>
                                View all reviews
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={styles.feedHeader}>
                              <div className={styles.feedHeaderText}>
                                <div className={styles.feedHeaderInnerText}>
                                  <span>{numeral(totalReviews).format('0,0')}</span> reviews sorted
                                  by{' '}
                                </div>
                                <div>
                                  {loading && <CircleLoader className={styles.mobileFeedLoader} />}
                                </div>
                              </div>
                              <div className={styles.feedHeaderFilter}>
                                <Select
                                  label=""
                                  defaultValue={feedSortBy}
                                  options={[
                                    {
                                      label: 'Most Relevant',
                                      value: ConsumerReviewSearchSort.RELEVANCE
                                    },
                                    {
                                      label: 'Most Recent',
                                      value: ConsumerReviewSearchSort.REVIEWED_AT
                                    }
                                  ]}
                                  onChange={event => {
                                    let enumValue
                                    switch (event.target.value) {
                                      case 'RELEVANCE':
                                        enumValue = ConsumerReviewSearchSort.RELEVANCE
                                        break

                                      case 'REVIEWED_AT':
                                        enumValue = ConsumerReviewSearchSort.REVIEWED_AT
                                        break
                                    }

                                    if (enumValue) {
                                      setFeedSortBy(enumValue)
                                    }
                                  }}
                                  display="simple"
                                />
                              </div>

                              {loading && <CircleLoader className={styles.desktopFeedLoader} />}
                            </div>
                            <div className={styles.feedCards}>
                              {edges.map(edge => (
                                <ActivityFeedCard
                                  key={edge.node.id}
                                  activityFeedItem={edge.node}
                                  className={styles.feedItem}
                                />
                              ))}
                            </div>
                            {hasNextPage && (
                              <div className={styles.fetchMore}>
                                {loading ? (
                                  <FullBlockLoader loader={CircleLoader} />
                                ) : (
                                  <div className={styles.fetchMoreButtonContainer}>
                                    <Button
                                      variant="secondary-outline"
                                      disabled={loading}
                                      onClick={customFetchMore}
                                    >
                                      Load more
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </FeedArea>
                    </>
                  )}
                </SidebarAwareGrid>
              </>
            )
          } else if (loading) {
            return <FullBlockLoader loader={CircleLoader} />
          } else {
            return null
          }
        }}
      </DepartmentCuratedActivityFeedComponent>
    </PageContent>
  )
}
export default ReviewsFeed
